//@flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import MinimalPersonnelFetchHOC from './MinimalPersonnelFetchHOC';
import { minimalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { useFormik } from 'formik';
import { RegExUtil } from 'common/utils/RegExUtil';
import {
    Stack,
    Card,
    Text,
    Button,
    Tooltip,
    Columns,
    Column,
    Divider
} from '@locus-taxy/neo-ui';
import { MinimalPersonnel } from 'common/models/internal/MinimalPersonnel';
import PasswordStrength, { usePasswordStrength } from './PasswordStrength';
import PasswordField from 'common/components/core/password/PasswordField';
import { setPersonnelPassword } from 'platform/api';
import { ErrorMessage } from 'common/components/core/input/ErrorMessage';
import noop from 'lodash/noop';
import forgotPasswordIds from 'common/tests/forgotPassword/forgotPasswordIds.json';

type OwnProps = {
    username: string,
    existingPassword: string,
    onSuccess: () => void,
    onBack: () => void
};

type Props = {
    ...OwnProps,
    minimalPersonnel: MinimalPersonnel
};

const ForgotPasswordForm = (props: Props) => {
    const {
        username,
        existingPassword,
        minimalPersonnel,
        onSuccess,
        onBack
    } = props;
    const { t } = useTranslation('settings');
    const [passwordApiError, setPasswordApiError] = React.useState<
        string | null
    >(null);
    const [formState, setFormState] = React.useState<
        'IDLE' | 'SETTING_PASSWORD' | 'SUCCESS' | 'FAILED'
    >('IDLE');
    React.useEffect(() => {
        if (formState === 'SUCCESS' && onSuccess) {
            const _timer = setTimeout(() => onSuccess(), 4000);
            return () => clearTimeout(_timer);
        }
    }, [formState, onBack, onSuccess]);
    const form = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: ''
        },
        validate: values => {
            const errors = {};
            const { confirmPassword, newPassword } = values;

            // validate newPassword
            if (newPassword) {
                if (!RegExUtil.isASCII(newPassword)) {
                    errors.newPassword = t('errorMessage.nonASCII');
                }
            } else {
                errors.newPassword = t('errorMessage.required');
            }

            // validate confirmPassword
            if (confirmPassword) {
                if (confirmPassword !== newPassword) {
                    errors.confirmPassword = t('errorMessage.confirmPassword');
                }
            } else {
                errors.confirmPassword = t('errorMessage.required');
            }

            return errors;
        },
        onSubmit: async values => {
            setPasswordApiError(null);
            setFormState('SETTING_PASSWORD');
            try {
                await setPersonnelPassword.post(
                    {
                        clientId: minimalPersonnel.clientId,
                        personnelId: minimalPersonnel.personnelId
                    },
                    {
                        existingPassword,
                        newPassword: values.newPassword
                    }
                );
                setPasswordApiError(null);
                setFormState('SUCCESS');
                return true;
            } catch (_err) {
                setPasswordApiError(
                    _err.message || t('forgotPasswordErrorDefault')
                );
                setFormState('FAILED');
                return false;
            }
        }
    });

    const { newPassword } = form.values;
    const strength = usePasswordStrength({
        password: newPassword,
        minimalPersonnel
    });
    const isFormValid = form.isValid && strength.verified;
    const disableSubmit =
        !isFormValid || form.isSubmitting || formState !== 'IDLE';

    const renderPasswordField = ({
        id,
        autocomplete,
        autoFocus = false,
        testId
    }: {
        id: string,
        autocomplete: string,
        autoFocus?: boolean,
        testId: ?string
    }) => {
        return (
            <PasswordField
                label={t(id)}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={value => {
                    form.handleChange(id)(value);
                    form.setTouched(
                        { ...form.touched, [id]: Boolean(value) },
                        false
                    );
                    // post getting an error, if form is touched, reset state to IDLE
                    if (formState === 'FAILED') {
                        setFormState('IDLE');
                    }
                }}
                errorMessage={form.touched[id] ? form.errors[id] : null}
                value={form.values[id]}
                autocomplete={autocomplete}
                autoFocus={autoFocus}
                testId={testId}
            />
        );
    };

    const renderStatusArea = () => {
        switch (formState) {
            case 'SUCCESS': {
                return (
                    <Text
                        fontSize={2}
                        textAlign="center"
                        data-test-id={
                            forgotPasswordIds.forgotPasswordActionStatus.success
                        }
                    >
                        {t('newPasswordSet')}
                    </Text>
                );
            }
            case 'FAILED': {
                if (passwordApiError) {
                    return (
                        <ErrorMessage
                            testId={
                                forgotPasswordIds.forgotPasswordActionStatus
                                    .failure
                            }
                        >
                            {passwordApiError}
                        </ErrorMessage>
                    );
                } else {
                    return null;
                }
            }
        }
    };

    return (
        <form onSubmit={disableSubmit ? noop : form.handleSubmit}>
            <Card>
                <Stack space="large">
                    <Text
                        fontSize={3}
                        textAlign="center"
                        data-test-id={forgotPasswordIds.title}
                    >
                        {t('resetPasswordMessage', {
                            username
                        })}
                    </Text>
                    <Divider color="grey.200" />
                    <Columns align="center" alignY="top" space="large">
                        <Column width="fluid">
                            <Stack space="large">
                                {renderPasswordField({
                                    id: 'newPassword',
                                    autocomplete: 'new-password',
                                    testId: forgotPasswordIds.newPassword
                                })}
                                {renderPasswordField({
                                    id: 'confirmPassword',
                                    autocomplete: 'new-password',
                                    testId: forgotPasswordIds.confirmPassword
                                })}
                                {renderStatusArea()}
                                <Columns
                                    alignY="center"
                                    align="right"
                                    space="xSmall"
                                >
                                    <Button
                                        onClick={onBack}
                                        appearance="ghost"
                                        size="medium"
                                        disabled={
                                            formState === 'SETTING_PASSWORD'
                                        }
                                        data-test-id={
                                            forgotPasswordIds.buttons.cancel
                                        }
                                    >
                                        {t('goBackToLogin')}
                                    </Button>
                                    <Tooltip
                                        tooltip={
                                            !isFormValid &&
                                            t('errorMessage.invalidPassword')
                                        }
                                        placement="top"
                                        shouldWrapChildren
                                    >
                                        <Button
                                            autoFocus
                                            appearance="solid"
                                            disabled={disableSubmit}
                                            palette="primary"
                                            type="submit"
                                            isLoading={
                                                formState === 'SETTING_PASSWORD'
                                            }
                                            data-test-id={
                                                forgotPasswordIds.buttons
                                                    .confirm
                                            }
                                        >
                                            {t('confirmPassword')}
                                        </Button>
                                    </Tooltip>
                                </Columns>
                            </Stack>
                        </Column>
                        <Column width="fluid">
                            <PasswordStrength result={strength} />
                        </Column>
                    </Columns>
                </Stack>
            </Card>
        </form>
    );
};

export default (compose(
    MinimalPersonnelFetchHOC,
    connect(state => {
        return {
            minimalPersonnel: minimalPersonnelSelector(state)
        };
    })
)(ForgotPasswordForm): React.AbstractComponent<OwnProps>);
